import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Terminal, Play, Loader2, Brain, Zap, Activity, Sparkles } from 'lucide-react';
import XtermTerminal from './XtermTerminal';

const ACCENT = '#00FF88';
const ACCENT_DIM = 'rgba(0,255,136,0.12)';
const ACCENT_LINE = 'rgba(0,255,136,0.35)';

const TERMINAL_LINE_COLOR = {
  system: 'text-[#00BFFF]',
  prompt: 'text-[#00FF00]',
  input: 'text-white',
  success: 'text-[#00FF00]',
  error: 'text-[#FF0055]',
  info: 'text-[#FF9500]',
};
const terminalLineClass = (type) => TERMINAL_LINE_COLOR[type] || 'text-gray-300';

/**
 * Soft contextual banner shown ABOVE the live terminal for Free users.
 * Framed as "earned access" — no Pro-gate language. Hidden for Pro entirely.
 */
const FreeSessionBanner = ({ creditsState }) => {
  if (!creditsState || creditsState.isPro) return null;
  const { balance, totalCap, lowCreditNudge } = creditsState;
  return (
    <div
      data-testid="free-session-banner"
      className="mb-3 flex items-center justify-between rounded-md px-3 py-2 text-xs"
      style={{ backgroundColor: ACCENT_DIM, borderLeft: `2px solid ${ACCENT}` }}
    >
      <span className="flex items-center gap-2 text-gray-300">
        <Activity className="w-3.5 h-3.5" style={{ color: ACCENT }} />
        Free sandbox session active &mdash;{' '}
        <span style={{ color: ACCENT }} className="font-semibold">
          {balance} min
        </span>{' '}
        available this month
      </span>
      {lowCreditNudge && (
        <span className="flex items-center gap-1 text-[11px]" style={{ color: ACCENT }}>
          <Sparkles className="w-3 h-3" /> Earn more on Dashboard
        </span>
      )}
      <span className="text-[10px] text-gray-500 hidden sm:inline">{totalCap} max</span>
    </div>
  );
};

/**
 * Out-of-credits state — shown only when an authenticated Free user has
 * 0 sandbox minutes left for the month. Per spec: soft nudge, never modal.
 * Pro upsell is one of several options, not the headline.
 */
const OutOfCreditsPanel = ({ onUpgrade }) => (
  <div
    data-testid="out-of-credits-panel"
    className="rounded-lg border p-6 text-center"
    style={{ borderColor: ACCENT_LINE, backgroundColor: '#0F1A12' }}
  >
    <div
      className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
      style={{ backgroundColor: ACCENT_DIM }}
    >
      <Activity className="w-6 h-6" style={{ color: ACCENT }} />
    </div>
    <h3 className="text-lg font-semibold text-white mb-1">
      All caught up for this month
    </h3>
    <p className="text-sm text-gray-400 max-w-md mx-auto mb-4">
      You&apos;ve used this month&apos;s sandbox minutes. Keep your streak,
      complete another room, or refer a friend to earn more time — or unlock
      unlimited sessions with Pro.
    </p>
    <div className="flex items-center justify-center gap-2">
      <Button
        variant="outline"
        size="sm"
        className="border-gray-700 text-gray-300 hover:bg-white/5"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        data-testid="oop-earn-more-btn"
      >
        Earn more time
      </Button>
      <Button
        size="sm"
        onClick={onUpgrade}
        className="bg-[#00FF88] text-black hover:opacity-90 font-semibold"
        data-testid="oop-upgrade-btn"
      >
        <Zap className="w-3.5 h-3.5 mr-1.5" />
        Unlock unlimited &mdash; $15/mo
      </Button>
    </div>
  </div>
);

const AnonMockTerminal = ({
  terminalOutput,
  command,
  onCommandChange,
  onCommandSubmit,
  onKeyDown,
  roomStarted,
  aiLoading,
}) => (
  <>
    <div className="bg-[#0A0A0A] rounded-lg p-4 font-mono text-sm h-96 overflow-y-auto">
      {terminalOutput.map((line, index) => (
        <div
          key={`term-${index}-${line.text.slice(0, 12)}`}
          className={`mb-1 ${terminalLineClass(line.type)}`}
        >
          {line.text.split('\n').map((text, i) => (
            <div key={`${index}-${i}`}>{text}</div>
          ))}
        </div>
      ))}
    </div>
    <form onSubmit={onCommandSubmit} className="mt-4">
      <div className="flex gap-2">
        <Input
          data-testid="terminal-input"
          value={command}
          onChange={(e) => onCommandChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Enter command... (try: help, tasks, hint, nmap 10.10.10.5)"
          disabled={!roomStarted || aiLoading}
          className="bg-[#0A0A0A] border-gray-800 text-white font-mono placeholder:text-gray-500"
        />
        <Button
          data-testid="terminal-execute-btn"
          type="submit"
          disabled={!roomStarted || aiLoading}
          className="bg-[#00FF00] text-black hover:bg-[#00DD00]"
        >
          {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Execute'}
        </Button>
      </div>
    </form>
  </>
);

/**
 * Big "Virtual Terminal" Card.
 *
 * Render paths (matches the credit engine):
 *   - Pro                                  → real xterm.js (unlimited).
 *   - Auth Free with credits > 0           → real xterm.js + FreeSessionBanner.
 *   - Auth Free with credits == 0          → soft OutOfCreditsPanel.
 *   - Anonymous                            → legacy mock terminal preview.
 */
export const RoomTerminalCard = ({
  room,
  roomId,
  isAuthenticated,
  isPro,
  roomStarted,
  terminalOutput,
  command,
  onCommandChange,
  onCommandSubmit,
  onKeyDown,
  onStartRoom,
  onAnalyzeThreat,
  aiLoading,
  creditsState,
  creditsLoading,
}) => {
  const navigate = useNavigate();
  const isAuthFree = isAuthenticated && !isPro;
  const balance = creditsState?.balance ?? 0;
  const hasCredits = isPro || balance > 0;

  return (
    <Card className="bg-[#1A1A1A] border-gray-800" data-testid="room-terminal-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Terminal className="w-5 h-5 text-[#00FF00]" />
            <CardTitle className="text-white">Virtual Terminal</CardTitle>
          </div>
          <div className="flex gap-2">
            {room.type === 'AI Challenge' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onAnalyzeThreat('Sample security logs for analysis')}
                disabled={aiLoading || !roomStarted}
                className="border-[#00BFFF] text-[#00BFFF] hover:bg-[#00BFFF]/10"
                data-testid="ai-analyze-btn"
              >
                {aiLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Brain className="w-4 h-4 mr-2" />
                )}
                AI Analyze
              </Button>
            )}
            <Button
              size="sm"
              onClick={onStartRoom}
              disabled={roomStarted}
              className="bg-[#00FF00] text-black hover:bg-[#00DD00]"
              data-testid="start-machine-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              {roomStarted ? 'Machine Running' : 'Start Machine'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!isAuthenticated ? (
          <AnonMockTerminal
            terminalOutput={terminalOutput}
            command={command}
            onCommandChange={onCommandChange}
            onCommandSubmit={onCommandSubmit}
            onKeyDown={onKeyDown}
            roomStarted={roomStarted}
            aiLoading={aiLoading}
          />
        ) : creditsLoading ? (
          <div className="h-72 flex items-center justify-center text-gray-500 text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Initialising sandbox…
          </div>
        ) : hasCredits ? (
          <>
            {isAuthFree && <FreeSessionBanner creditsState={creditsState} />}
            <div data-testid={isPro ? 'terminal-pro-xterm' : 'terminal-free-xterm'}>
              <XtermTerminal height={420} roomId={roomId} />
            </div>
          </>
        ) : (
          <OutOfCreditsPanel onUpgrade={() => navigate('/pricing')} />
        )}
      </CardContent>
    </Card>
  );
};
